/**
 * AAR Refueling Simulation Engine
 *
 * Pure TypeScript — scheduling, fuel math, voice comms, contingencies.
 * No React dependencies. Mirrors cascadeEngine.ts pattern.
 */

import type {
  AARReceiver,
  AARTanker,
  ARTrack,
  AARScenarioConfig,
  AARSimState,
  VoiceCommEntry,
  AARContingency,
  ReceiverPriority,
  FuelState,
} from '../types'

// ===== Priority ordering =====
const PRIORITY_ORDER: Record<ReceiverPriority, number> = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,
}

// ===== Asset Defaults Database =====
export const ASSET_DEFAULTS: Record<string, { fuelCapacity: number; burnRate: number; bingoFuel: number; role: 'receiver' | 'tanker'; flowRate?: number; defaultOffload?: number }> = {
  'F-22':   { fuelCapacity: 18000, burnRate: 12000, bingoFuel: 5400, role: 'receiver' },
  'F-35':   { fuelCapacity: 18500, burnRate: 12000, bingoFuel: 5500, role: 'receiver' },
  'F-16':   { fuelCapacity: 7000,  burnRate: 5800,  bingoFuel: 2100, role: 'receiver' },
  'F-15E':  { fuelCapacity: 13100, burnRate: 10200, bingoFuel: 3900, role: 'receiver' },
  'A-10':   { fuelCapacity: 11000, burnRate: 4200,  bingoFuel: 3300, role: 'receiver' },
  'F-18':   { fuelCapacity: 14400, burnRate: 10800, bingoFuel: 4300, role: 'receiver' },
  'E-3':    { fuelCapacity: 155000, burnRate: 12000, bingoFuel: 46500, role: 'receiver' },
  'E-2':    { fuelCapacity: 12400, burnRate: 3000,  bingoFuel: 3700, role: 'receiver' },
  'B-1':    { fuelCapacity: 265000, burnRate: 30000, bingoFuel: 79500, role: 'receiver' },
  'B-2':    { fuelCapacity: 167000, burnRate: 18000, bingoFuel: 50100, role: 'receiver' },
  'B-52':   { fuelCapacity: 312000, burnRate: 16000, bingoFuel: 93600, role: 'receiver' },
  'KC-135': { fuelCapacity: 200000, burnRate: 10000, bingoFuel: 40000, role: 'tanker', flowRate: 900, defaultOffload: 120000 },
  'KC-46':  { fuelCapacity: 212000, burnRate: 10000, bingoFuel: 42000, role: 'tanker', flowRate: 1200, defaultOffload: 210000 },
}

// ===== Scenario Presets =====

function pacificSurge(): AARScenarioConfig {
  return {
    name: 'PACIFIC SURGE — 4-Ship SEAD Package',
    timeWindowMin: 150,
    startTimeZulu: '0200',
    arTracks: [
      { id: 'AR-301', name: 'AR-301 ORANGE', altitude: 'FL250-FL280', tankerIds: ['SHELL-61'] },
      { id: 'AR-302', name: 'AR-302 PURPLE', altitude: 'FL240-FL270', tankerIds: ['ARCO-22'] },
    ],
    tankers: [
      {
        id: 'SHELL-61', callsign: 'SHELL 61', assetType: 'KC-135R',
        totalOffload: 120000, offloaded: 0, flowRate: 900,
        arTrack: 'AR-301 ORANGE', availableWindow: [0, 130],
        status: 'AVAILABLE', queuedReceivers: [], currentReceiver: null,
      },
      {
        id: 'ARCO-22', callsign: 'ARCO 22', assetType: 'KC-135R',
        totalOffload: 115000, offloaded: 0, flowRate: 900,
        arTrack: 'AR-302 PURPLE', availableWindow: [10, 140],
        status: 'AVAILABLE', queuedReceivers: [], currentReceiver: null,
      },
    ],
    receivers: [
      {
        id: 'VIPER-01', callsign: 'VIPER 01', assetType: 'F-16C/D Viper',
        fuelCapacity: 7000, currentFuel: 4200, burnRate: 5800,
        bingoFuel: 2100, jokerFuel: 2800, requestedOffload: 5500,
        flightSize: 2, priority: 'HIGH', missionEssential: true, arctWindow: [15, 45],
        status: 'WAITING',
      },
      {
        id: 'VIPER-02', callsign: 'VIPER 02', assetType: 'F-16C/D Viper',
        fuelCapacity: 7000, currentFuel: 4000, burnRate: 5800,
        bingoFuel: 2100, jokerFuel: 2800, requestedOffload: 5500,
        flightSize: 2, priority: 'HIGH', missionEssential: true, arctWindow: [15, 50],
        status: 'WAITING',
      },
      {
        id: 'VIPER-03', callsign: 'VIPER 03', assetType: 'F-16C/D Viper',
        fuelCapacity: 7000, currentFuel: 4400, burnRate: 5600,
        bingoFuel: 2100, jokerFuel: 2800, requestedOffload: 5000,
        flightSize: 1, priority: 'MEDIUM', missionEssential: false, arctWindow: [30, 70],
        status: 'WAITING',
      },
      {
        id: 'VIPER-04', callsign: 'VIPER 04', assetType: 'F-16C/D Viper',
        fuelCapacity: 7000, currentFuel: 4600, burnRate: 5600,
        bingoFuel: 2100, jokerFuel: 2800, requestedOffload: 4800,
        flightSize: 1, priority: 'MEDIUM', missionEssential: false, arctWindow: [40, 80],
        status: 'WAITING',
      },
    ],
  }
}

function natoStrike(): AARScenarioConfig {
  return {
    name: 'NATO STRIKE PKG — Large Force Employment',
    timeWindowMin: 180,
    startTimeZulu: '0430',
    arTracks: [
      { id: 'AR-401', name: 'AR-401 RED', altitude: 'FL260-FL290', tankerIds: ['TEXACO-71'] },
      { id: 'AR-402', name: 'AR-402 BLUE', altitude: 'FL250-FL280', tankerIds: ['SHELL-42'] },
      { id: 'AR-403', name: 'AR-403 GREEN', altitude: 'FL240-FL270', tankerIds: ['ARCO-15'] },
    ],
    tankers: [
      {
        id: 'TEXACO-71', callsign: 'TEXACO 71', assetType: 'KC-46A',
        totalOffload: 210000, offloaded: 0, flowRate: 1200,
        arTrack: 'AR-401 RED', availableWindow: [0, 160],
        status: 'AVAILABLE', queuedReceivers: [], currentReceiver: null,
      },
      {
        id: 'SHELL-42', callsign: 'SHELL 42', assetType: 'KC-46A',
        totalOffload: 210000, offloaded: 0, flowRate: 1200,
        arTrack: 'AR-402 BLUE', availableWindow: [5, 165],
        status: 'AVAILABLE', queuedReceivers: [], currentReceiver: null,
      },
      {
        id: 'ARCO-15', callsign: 'ARCO 15', assetType: 'KC-135R',
        totalOffload: 120000, offloaded: 0, flowRate: 900,
        arTrack: 'AR-403 GREEN', availableWindow: [15, 150],
        status: 'AVAILABLE', queuedReceivers: [], currentReceiver: null,
      },
    ],
    receivers: [
      {
        id: 'RAPTOR-11', callsign: 'RAPTOR 11', assetType: 'F-15E Strike Eagle',
        fuelCapacity: 13100, currentFuel: 7800, burnRate: 10200,
        bingoFuel: 3900, jokerFuel: 5200, requestedOffload: 9500,
        flightSize: 2, priority: 'HIGH', missionEssential: true, arctWindow: [10, 40],
        status: 'WAITING',
      },
      {
        id: 'RAPTOR-12', callsign: 'RAPTOR 12', assetType: 'F-15E Strike Eagle',
        fuelCapacity: 13100, currentFuel: 8200, burnRate: 10200,
        bingoFuel: 3900, jokerFuel: 5200, requestedOffload: 9000,
        flightSize: 2, priority: 'HIGH', missionEssential: true, arctWindow: [10, 45],
        status: 'WAITING',
      },
      {
        id: 'VENOM-21', callsign: 'VENOM 21', assetType: 'F-16C/D Viper',
        fuelCapacity: 7000, currentFuel: 4100, burnRate: 5800,
        bingoFuel: 2100, jokerFuel: 2800, requestedOffload: 5500,
        flightSize: 2, priority: 'MEDIUM', missionEssential: true, arctWindow: [25, 60],
        status: 'WAITING',
      },
      {
        id: 'VENOM-22', callsign: 'VENOM 22', assetType: 'F-16C/D Viper',
        fuelCapacity: 7000, currentFuel: 4300, burnRate: 5800,
        bingoFuel: 2100, jokerFuel: 2800, requestedOffload: 5200,
        flightSize: 1, priority: 'MEDIUM', missionEssential: false, arctWindow: [30, 65],
        status: 'WAITING',
      },
      {
        id: 'BOLT-31', callsign: 'BOLT 31', assetType: 'F-35A Lightning II',
        fuelCapacity: 18500, currentFuel: 11000, burnRate: 12000,
        bingoFuel: 5500, jokerFuel: 7400, requestedOffload: 13000,
        flightSize: 2, priority: 'MEDIUM', missionEssential: true, arctWindow: [40, 80],
        status: 'WAITING',
      },
      {
        id: 'BOLT-32', callsign: 'BOLT 32', assetType: 'F-35A Lightning II',
        fuelCapacity: 18500, currentFuel: 11500, burnRate: 12000,
        bingoFuel: 5500, jokerFuel: 7400, requestedOffload: 12500,
        flightSize: 1, priority: 'LOW', missionEssential: false, arctWindow: [50, 90],
        status: 'WAITING',
      },
    ],
  }
}

function centcomCAS(): AARScenarioConfig {
  return {
    name: 'CENTCOM CAS — Long-Endurance Rotation',
    timeWindowMin: 210,
    startTimeZulu: '0600',
    arTracks: [
      { id: 'AR-501', name: 'AR-501 GOLD', altitude: 'FL200-FL230', tankerIds: ['IRON-81', 'IRON-82'] },
    ],
    tankers: [
      {
        id: 'IRON-81', callsign: 'IRON 81', assetType: 'KC-10A',
        totalOffload: 340000, offloaded: 0, flowRate: 1500,
        arTrack: 'AR-501 GOLD', availableWindow: [0, 180],
        status: 'AVAILABLE', queuedReceivers: [], currentReceiver: null,
      },
      {
        id: 'IRON-82', callsign: 'IRON 82', assetType: 'KC-10A',
        totalOffload: 340000, offloaded: 0, flowRate: 1500,
        arTrack: 'AR-501 GOLD', availableWindow: [20, 200],
        status: 'AVAILABLE', queuedReceivers: [], currentReceiver: null,
      },
    ],
    receivers: [
      {
        id: 'HOG-11', callsign: 'HOG 11', assetType: 'A-10C Thunderbolt II',
        fuelCapacity: 11000, currentFuel: 6600, burnRate: 4200,
        bingoFuel: 3300, jokerFuel: 4400, requestedOffload: 8000,
        flightSize: 2, priority: 'LOW', missionEssential: false, arctWindow: [20, 60],
        status: 'WAITING',
      },
      {
        id: 'HOG-12', callsign: 'HOG 12', assetType: 'A-10C Thunderbolt II',
        fuelCapacity: 11000, currentFuel: 7000, burnRate: 4200,
        bingoFuel: 3300, jokerFuel: 4400, requestedOffload: 7500,
        flightSize: 2, priority: 'LOW', missionEssential: false, arctWindow: [25, 65],
        status: 'WAITING',
      },
      {
        id: 'HOG-13', callsign: 'HOG 13', assetType: 'A-10C Thunderbolt II',
        fuelCapacity: 11000, currentFuel: 6200, burnRate: 4200,
        bingoFuel: 3300, jokerFuel: 4400, requestedOffload: 8500,
        flightSize: 1, priority: 'LOW', missionEssential: false, arctWindow: [60, 110],
        status: 'WAITING',
      },
      {
        id: 'HOG-14', callsign: 'HOG 14', assetType: 'A-10C Thunderbolt II',
        fuelCapacity: 11000, currentFuel: 6800, burnRate: 4200,
        bingoFuel: 3300, jokerFuel: 4400, requestedOffload: 7800,
        flightSize: 1, priority: 'LOW', missionEssential: false, arctWindow: [70, 120],
        status: 'WAITING',
      },
      {
        id: 'RAGE-21', callsign: 'RAGE 21', assetType: 'F-15E Strike Eagle',
        fuelCapacity: 13100, currentFuel: 7500, burnRate: 10200,
        bingoFuel: 3900, jokerFuel: 5200, requestedOffload: 10000,
        flightSize: 2, priority: 'MEDIUM', missionEssential: true, arctWindow: [35, 75],
        status: 'WAITING',
      },
      {
        id: 'RAGE-22', callsign: 'RAGE 22', assetType: 'F-15E Strike Eagle',
        fuelCapacity: 13100, currentFuel: 7800, burnRate: 10200,
        bingoFuel: 3900, jokerFuel: 5200, requestedOffload: 9500,
        flightSize: 2, priority: 'MEDIUM', missionEssential: true, arctWindow: [40, 80],
        status: 'WAITING',
      },
    ],
  }
}

export function getScenarioPresets(): { id: string; name: string; description: string; config: AARScenarioConfig }[] {
  return [
    {
      id: 'pacific-surge',
      name: 'PACIFIC SURGE',
      description: '4-ship SEAD package, 2x KC-135R on separate tracks. High priority receivers with tight ARCT windows.',
      config: pacificSurge(),
    },
    {
      id: 'nato-strike',
      name: 'NATO STRIKE PKG',
      description: 'Large force package with 3 tankers (2x KC-46A + KC-135R) supporting 6 mixed receivers across 3 AR tracks.',
      config: natoStrike(),
    },
    {
      id: 'centcom-cas',
      name: 'CENTCOM CAS',
      description: 'Long-endurance CAS rotation with 2x KC-10A heavy tankers supporting 4x A-10 + 2x F-15E on single track.',
      config: centcomCAS(),
    },
  ]
}

/** Build a default scenario from API fuel states */
export function createDefaultScenario(fuelStates: FuelState[]): AARScenarioConfig {
  const airAssets = fuelStates.filter(f => f.domain === 'AIR').slice(0, 4)
  const receivers: AARReceiver[] = airAssets.map((f, i) => ({
    id: `RCV-${String(i + 1).padStart(2, '0')}`,
    callsign: `RECV ${String(i + 1).padStart(2, '0')}`,
    assetType: f.asset_type,
    fuelCapacity: f.capacity_lbs,
    currentFuel: f.current_lbs,
    burnRate: f.burn_rate_lbs_hr,
    bingoFuel: f.bingo_fuel_lbs,
    jokerFuel: f.bingo_fuel_lbs * 1.3,
    requestedOffload: f.capacity_lbs * 0.8,
    flightSize: 1,
    priority: i < 2 ? 'HIGH' as ReceiverPriority : 'MEDIUM' as ReceiverPriority,
    missionEssential: i < 2,
    arctWindow: [15 + i * 15, 60 + i * 15] as [number, number],
    status: 'WAITING' as const,
  }))

  return {
    name: 'CUSTOM — From Theater Data',
    timeWindowMin: 150,
    startTimeZulu: '0000',
    arTracks: [{ id: 'AR-100', name: 'AR-100 ALPHA', altitude: 'FL250-FL280', tankerIds: ['TNK-01'] }],
    tankers: [{
      id: 'TNK-01', callsign: 'TANKER 01', assetType: 'KC-135R',
      totalOffload: 120000, offloaded: 0, flowRate: 900,
      arTrack: 'AR-100 ALPHA', availableWindow: [0, 140],
      status: 'AVAILABLE', queuedReceivers: [], currentReceiver: null,
    }],
    receivers,
  }
}

// ===== Initialize simulation state =====
export function initSimState(config: AARScenarioConfig): AARSimState {
  const state: AARSimState = {
    currentTime: 0,
    isRunning: false,
    speed: 1,
    tankers: config.tankers.map(t => ({ ...t, offloaded: 0, queuedReceivers: [], currentReceiver: null, status: 'AVAILABLE' as const })),
    receivers: config.receivers.map(r => ({ ...r, status: 'WAITING' as const })),
    voiceLog: [],
    contingencies: [],
    phase: 'SETUP',
    missionAborted: false,
  }
  return scheduleReceivers(state)
}

// ===== Receiver scheduling (priority-based greedy) =====
export function scheduleReceivers(state: AARSimState): AARSimState {
  const tankers = state.tankers.map(t => ({ ...t, queuedReceivers: [...t.queuedReceivers] }))
  const receivers = state.receivers.map(r => ({ ...r }))

  // Collect unassigned receivers (WAITING status, not already queued)
  const allQueued = new Set(tankers.flatMap(t => [t.currentReceiver, ...t.queuedReceivers].filter(Boolean)))
  const unassigned = receivers.filter(r =>
    r.status === 'WAITING' && !allQueued.has(r.id)
  )

  // Sort: missionEssential DESC → priority → earliest ARCT → fuel urgency
  unassigned.sort((a, b) => {
    if (a.missionEssential !== b.missionEssential) return a.missionEssential ? -1 : 1
    if (a.priority !== b.priority) return PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
    if (a.arctWindow[0] !== b.arctWindow[0]) return a.arctWindow[0] - b.arctWindow[0]
    return (a.currentFuel / a.fuelCapacity) - (b.currentFuel / b.fuelCapacity)
  })

  for (const rcv of unassigned) {
    // Find best tanker: available, has capacity, window overlaps
    let bestTanker: typeof tankers[0] | null = null
    let bestScore = -Infinity

    for (const tnk of tankers) {
      if (tnk.status === 'CANCELLED' || tnk.status === 'RTB') continue
      if (tnk.offloaded >= tnk.totalOffload) continue
      // Check window overlap
      const overlapStart = Math.max(tnk.availableWindow[0], rcv.arctWindow[0])
      const overlapEnd = Math.min(tnk.availableWindow[1], rcv.arctWindow[1])
      if (overlapStart >= overlapEnd) continue

      // Score: remaining capacity + fewer queued receivers = better
      const remainingCapacity = tnk.totalOffload - tnk.offloaded
      const queuePenalty = tnk.queuedReceivers.length * 10000
      const score = remainingCapacity - queuePenalty
      if (score > bestScore) {
        bestScore = score
        bestTanker = tnk
      }
    }

    if (bestTanker) {
      bestTanker.queuedReceivers.push(rcv.id)
      const rcvIdx = receivers.findIndex(r => r.id === rcv.id)
      if (rcvIdx >= 0) receivers[rcvIdx].status = 'IN_QUEUE'
    }
  }

  return { ...state, tankers, receivers }
}

// ===== Fuel projections =====
export function projectFuelState(receiver: AARReceiver, currentTime: number): {
  timeToJoker: number
  timeToBingo: number
  needsEmergency: boolean
} {
  const burnPerMin = receiver.burnRate / 60
  if (burnPerMin <= 0) return { timeToJoker: Infinity, timeToBingo: Infinity, needsEmergency: false }
  const fuelAboveJoker = receiver.currentFuel - receiver.jokerFuel
  const fuelAboveBingo = receiver.currentFuel - receiver.bingoFuel
  const timeToJoker = fuelAboveJoker > 0 ? fuelAboveJoker / burnPerMin : 0
  const timeToBingo = fuelAboveBingo > 0 ? fuelAboveBingo / burnPerMin : 0
  return { timeToJoker, timeToBingo, needsEmergency: receiver.currentFuel <= receiver.bingoFuel }
}

// ===== Voice comm generation =====
export function generateVoiceComm(prevState: AARSimState, newState: AARSimState): VoiceCommEntry[] {
  const comms: VoiceCommEntry[] = []
  const t = newState.currentTime

  for (const rcv of newState.receivers) {
    const prev = prevState.receivers.find(r => r.id === rcv.id)
    if (!prev) continue

    // Check-in when entering ARCT window
    if (prev.status === 'WAITING' && rcv.status === 'IN_QUEUE') {
      const tanker = newState.tankers.find(tnk =>
        tnk.queuedReceivers.includes(rcv.id) || tnk.currentReceiver === rcv.id
      )
      comms.push({
        simTime: t, from: rcv.callsign, to: tanker?.callsign ?? 'CONTROL',
        message: `${rcv.callsign}, checking in ${tanker?.arTrack ?? 'AR track'}, ${Math.round(rcv.currentFuel).toLocaleString()} lbs, requesting ${rcv.requestedOffload.toLocaleString()} lbs`,
        type: 'CHECK_IN',
      })
    }

    // Cleared to pre-contact
    if (prev.status === 'IN_QUEUE' && rcv.status === 'ON_BOOM') {
      const tanker = newState.tankers.find(tnk => tnk.currentReceiver === rcv.id)
      comms.push({
        simTime: t, from: tanker?.callsign ?? 'TANKER', to: rcv.callsign,
        message: `${rcv.callsign}, cleared to contact position`,
        type: 'CLEARED',
      })
      comms.push({
        simTime: t, from: rcv.callsign, to: tanker?.callsign ?? 'TANKER',
        message: `${rcv.callsign}, contact, taking fuel`,
        type: 'INFO',
      })
    }

    // Complete
    if (prev.status === 'ON_BOOM' && rcv.status === 'COMPLETE') {
      const tanker = prevState.tankers.find(tnk => tnk.currentReceiver === rcv.id)
      comms.push({
        simTime: t, from: rcv.callsign, to: tanker?.callsign ?? 'TANKER',
        message: `${rcv.callsign}, disconnect, ${Math.round(rcv.currentFuel).toLocaleString()} lbs, rejoining formation`,
        type: 'COMPLETE',
      })
    }

    // Emergency — bingo fuel
    if (prev.status !== 'EMERGENCY' && rcv.status === 'EMERGENCY') {
      comms.push({
        simTime: t, from: rcv.callsign, to: 'CONTROL',
        message: `MAYDAY MAYDAY, ${rcv.callsign} is EMERGENCY FUEL, ${Math.round(rcv.currentFuel).toLocaleString()} lbs, requesting immediate tanker`,
        type: 'EMERGENCY',
      })
    }

    // Joker warning
    if (prev.currentFuel > rcv.jokerFuel && rcv.currentFuel <= rcv.jokerFuel && rcv.status !== 'EMERGENCY' && rcv.status !== 'COMPLETE') {
      comms.push({
        simTime: t, from: rcv.callsign, to: 'CONTROL',
        message: `${rcv.callsign}, JOKER fuel, ${Math.round(rcv.currentFuel).toLocaleString()} lbs`,
        type: 'INFO',
      })
    }
  }

  // Tanker status changes
  for (const tnk of newState.tankers) {
    const prev = prevState.tankers.find(t => t.id === tnk.id)
    if (!prev) continue
    if (prev.status !== 'CANCELLED' && tnk.status === 'CANCELLED') {
      comms.push({
        simTime: t, from: tnk.callsign, to: 'CONTROL',
        message: `${tnk.callsign} is RTB, tanker operations cancelled`,
        type: 'CONTINGENCY',
      })
    }
    if (prev.status !== 'RTB' && tnk.status === 'RTB') {
      comms.push({
        simTime: t, from: tnk.callsign, to: 'CONTROL',
        message: `${tnk.callsign}, offload complete, RTB`,
        type: 'INFO',
      })
    }
  }

  return comms
}

// ===== Core tick — advance simulation by 1 minute =====
export function tickSimulation(state: AARSimState): AARSimState {
  if (state.phase !== 'RUNNING') return state

  const t = state.currentTime + 1
  let tankers = state.tankers.map(tnk => ({ ...tnk, queuedReceivers: [...tnk.queuedReceivers] }))
  let receivers = state.receivers.map(rcv => ({ ...rcv }))

  // 1. Burn fuel for all non-complete, non-diverted receivers
  for (const rcv of receivers) {
    if (rcv.status === 'COMPLETE' || rcv.status === 'DIVERTED') continue
    const burnPerMin = rcv.burnRate / 60
    // Receivers on boom still burn, but gain fuel from tanker (handled below)
    rcv.currentFuel = Math.max(0, rcv.currentFuel - burnPerMin)
  }

  // 2. Refueling: transfer fuel from tanker to on-boom receiver
  for (const tnk of tankers) {
    if (tnk.status === 'CANCELLED' || tnk.status === 'RTB') continue
    if (!tnk.currentReceiver) continue

    const rcv = receivers.find(r => r.id === tnk.currentReceiver)
    if (!rcv || rcv.status !== 'ON_BOOM') continue

    const remainingCapacity = tnk.totalOffload - tnk.offloaded
    const transfer = Math.min(tnk.flowRate, remainingCapacity, rcv.fuelCapacity - rcv.currentFuel)
    rcv.currentFuel += transfer
    tnk.offloaded += transfer

    // Check completion: fuel nearly full OR offload met
    const fuelReceived = rcv.currentFuel - (state.receivers.find(r => r.id === rcv.id)?.currentFuel ?? 0) + (rcv.burnRate / 60)
    const totalOffloaded = rcv.fuelCapacity * 0.9
    if (rcv.currentFuel >= totalOffloaded || rcv.currentFuel >= rcv.fuelCapacity * 0.95) {
      rcv.status = 'COMPLETE'
      tnk.currentReceiver = null
    }

    // Tanker offload exhausted
    if (tnk.offloaded >= tnk.totalOffload) {
      if (rcv.status !== 'COMPLETE') {
        rcv.status = 'COMPLETE' // partial fill but tanker empty
      }
      tnk.currentReceiver = null
      tnk.status = 'RTB'
    }
  }

  // 3. Dequeue next receiver for tankers with no current receiver
  for (const tnk of tankers) {
    if (tnk.status === 'CANCELLED' || tnk.status === 'RTB') continue
    if (tnk.currentReceiver) continue
    if (tnk.queuedReceivers.length === 0) continue
    if (t < tnk.availableWindow[0] || t > tnk.availableWindow[1]) continue

    const nextId = tnk.queuedReceivers.shift()!
    const rcv = receivers.find(r => r.id === nextId)
    if (rcv && rcv.status === 'IN_QUEUE') {
      tnk.currentReceiver = nextId
      rcv.status = 'ON_BOOM'
    }
  }

  // 4. Check ARCT windows — queue waiting receivers whose window has arrived
  for (const rcv of receivers) {
    if (rcv.status !== 'WAITING') continue
    if (t >= rcv.arctWindow[0] && t <= rcv.arctWindow[1]) {
      // Try to schedule this receiver
      const allQueued = new Set(tankers.flatMap(tnk => [tnk.currentReceiver, ...tnk.queuedReceivers].filter(Boolean)))
      if (!allQueued.has(rcv.id)) {
        // Find a tanker for this receiver
        for (const tnk of tankers) {
          if (tnk.status === 'CANCELLED' || tnk.status === 'RTB') continue
          if (tnk.offloaded >= tnk.totalOffload) continue
          const overlapStart = Math.max(tnk.availableWindow[0], rcv.arctWindow[0])
          const overlapEnd = Math.min(tnk.availableWindow[1], rcv.arctWindow[1])
          if (overlapStart < overlapEnd) {
            tnk.queuedReceivers.push(rcv.id)
            rcv.status = 'IN_QUEUE'
            break
          }
        }
      }
    }
  }

  // 5. Bingo / Joker checks
  for (const rcv of receivers) {
    if (rcv.status === 'COMPLETE' || rcv.status === 'DIVERTED') continue
    if (rcv.currentFuel <= rcv.bingoFuel && rcv.status !== 'EMERGENCY') {
      rcv.status = 'EMERGENCY'
      // Bump to front of all tanker queues if in queue
      for (const tnk of tankers) {
        const idx = tnk.queuedReceivers.indexOf(rcv.id)
        if (idx > 0) {
          tnk.queuedReceivers.splice(idx, 1)
          tnk.queuedReceivers.unshift(rcv.id)
        }
      }
    } else if (rcv.currentFuel <= rcv.jokerFuel && rcv.priority !== 'HIGH') {
      rcv.priority = 'HIGH'
    }
  }

  // 6. Check tanker window expiry
  for (const tnk of tankers) {
    if (tnk.status === 'AVAILABLE' && t > tnk.availableWindow[1]) {
      tnk.status = 'RTB'
      if (tnk.currentReceiver) {
        const rcv = receivers.find(r => r.id === tnk.currentReceiver)
        if (rcv && rcv.status === 'ON_BOOM') rcv.status = 'COMPLETE'
        tnk.currentReceiver = null
      }
    }
  }

  // 7. Check if sim is complete (all receivers complete/diverted or all tankers done)
  const allReceiversDone = receivers.every(r => r.status === 'COMPLETE' || r.status === 'DIVERTED')
  const allTankersDone = tankers.every(t => t.status === 'RTB' || t.status === 'CANCELLED')

  const newState: AARSimState = {
    ...state,
    currentTime: t,
    tankers,
    receivers,
    phase: (allReceiversDone || allTankersDone) ? 'COMPLETE' : 'RUNNING',
  }

  // Generate voice comms
  const newComms = generateVoiceComm(state, newState)
  newState.voiceLog = [...state.voiceLog, ...newComms]

  return newState
}

// ===== Apply contingency =====
export function applyContingency(state: AARSimState, contingency: AARContingency): AARSimState {
  let tankers = state.tankers.map(t => ({ ...t, queuedReceivers: [...t.queuedReceivers] }))
  let receivers = state.receivers.map(r => ({ ...r }))
  const comms: VoiceCommEntry[] = []
  let missionAborted = false

  switch (contingency.type) {
    case 'TANKER_CANCEL': {
      const tnk = tankers.find(t => t.id === contingency.affectedId)
      if (tnk) {
        // Release current receiver
        if (tnk.currentReceiver) {
          const rcv = receivers.find(r => r.id === tnk.currentReceiver)
          if (rcv && rcv.status === 'ON_BOOM') rcv.status = 'WAITING'
          tnk.currentReceiver = null
        }
        // Release queued receivers
        for (const rcvId of tnk.queuedReceivers) {
          const rcv = receivers.find(r => r.id === rcvId)
          if (rcv && rcv.status === 'IN_QUEUE') rcv.status = 'WAITING'
        }
        tnk.queuedReceivers = []
        tnk.status = 'CANCELLED'

        comms.push({
          simTime: state.currentTime, from: 'CONTROL', to: 'ALL',
          message: `ATTENTION ALL AIRCRAFT, ${tnk.callsign} operations cancelled, receivers reflow to alternate tanker`,
          type: 'CONTINGENCY',
        })

        // Re-schedule displaced receivers
        const newState = scheduleReceivers({ ...state, tankers, receivers })
        tankers = newState.tankers
        receivers = newState.receivers
      }
      break
    }
    case 'RECEIVER_EMERGENCY': {
      const rcv = receivers.find(r => r.id === contingency.affectedId)
      if (rcv && rcv.status !== 'COMPLETE' && rcv.status !== 'DIVERTED') {
        rcv.status = 'EMERGENCY'
        rcv.priority = 'HIGH'
        rcv.missionEssential = true
        // Bump to front of queue
        for (const tnk of tankers) {
          const idx = tnk.queuedReceivers.indexOf(rcv.id)
          if (idx > 0) {
            tnk.queuedReceivers.splice(idx, 1)
            tnk.queuedReceivers.unshift(rcv.id)
          }
          // If no tanker has this receiver, assign to first available
          if (idx === -1 && !tnk.queuedReceivers.includes(rcv.id) && tnk.currentReceiver !== rcv.id) {
            if (tnk.status === 'AVAILABLE' || tnk.status === 'REFUELING') {
              tnk.queuedReceivers.unshift(rcv.id)
              break
            }
          }
        }
        comms.push({
          simTime: state.currentTime, from: rcv.callsign, to: 'CONTROL',
          message: `MAYDAY MAYDAY, ${rcv.callsign} declaring EMERGENCY, requesting priority refueling`,
          type: 'EMERGENCY',
        })
      }
      break
    }
    case 'FUEL_SHORTFALL': {
      const tnk = tankers.find(t => t.id === contingency.affectedId)
      if (tnk) {
        const reduction = tnk.totalOffload * 0.3
        tnk.totalOffload = Math.max(tnk.offloaded, tnk.totalOffload - reduction)
        comms.push({
          simTime: state.currentTime, from: tnk.callsign, to: 'CONTROL',
          message: `${tnk.callsign}, fuel shortfall, reducing available offload by ${Math.round(reduction).toLocaleString()} lbs`,
          type: 'CONTINGENCY',
        })
      }
      break
    }
    case 'WEATHER_DIVERT': {
      const rcv = receivers.find(r => r.id === contingency.affectedId)
      if (rcv && rcv.status !== 'COMPLETE') {
        rcv.status = 'DIVERTED'
        // Remove from tanker queues
        for (const tnk of tankers) {
          if (tnk.currentReceiver === rcv.id) tnk.currentReceiver = null
          tnk.queuedReceivers = tnk.queuedReceivers.filter(id => id !== rcv.id)
        }
        comms.push({
          simTime: state.currentTime, from: rcv.callsign, to: 'CONTROL',
          message: `${rcv.callsign}, weather divert, departing AR track`,
          type: 'CONTINGENCY',
        })
      }
      break
    }
    case 'TANKER_RTB_EARLY': {
      const tnk = tankers.find(t => t.id === contingency.affectedId)
      if (tnk && tnk.status !== 'CANCELLED' && tnk.status !== 'RTB') {
        const rtbMin = contingency.rtbMinutes ?? 0
        // Set tanker window to end at currentTime + rtbMinutes
        tnk.availableWindow = [tnk.availableWindow[0], state.currentTime + rtbMin]

        if (rtbMin === 0) {
          // Immediate RTB after current receiver — drop queue
          for (const rcvId of tnk.queuedReceivers) {
            const rcv = receivers.find(r => r.id === rcvId)
            if (rcv && rcv.status === 'IN_QUEUE') rcv.status = 'WAITING'
          }
          tnk.queuedReceivers = []
          // If no current receiver, RTB now
          if (!tnk.currentReceiver) tnk.status = 'RTB'
        } else {
          // Release queued receivers that can't be serviced in time
          const estimatedEndTime = state.currentTime + rtbMin
          let runningTime = state.currentTime
          // Keep only receivers we can finish before RTB
          const keepQueue: string[] = []
          for (const rcvId of tnk.queuedReceivers) {
            const rcv = receivers.find(r => r.id === rcvId)
            if (!rcv) continue
            const refuelTime = rcv.requestedOffload / tnk.flowRate
            if (runningTime + refuelTime <= estimatedEndTime) {
              keepQueue.push(rcvId)
              runningTime += refuelTime
            } else {
              if (rcv.status === 'IN_QUEUE') rcv.status = 'WAITING'
            }
          }
          tnk.queuedReceivers = keepQueue
        }

        comms.push({
          simTime: state.currentTime, from: tnk.callsign, to: 'CONTROL',
          message: `${tnk.callsign}, RTB in ${rtbMin} minutes${rtbMin === 0 ? ' — completing current receiver then RTB' : ''}`,
          type: 'CONTINGENCY',
        })

        // Re-schedule displaced receivers
        const reflow = scheduleReceivers({ ...state, tankers, receivers })
        tankers.forEach((t, i) => { t.queuedReceivers = reflow.tankers[i].queuedReceivers })
        receivers.forEach((r, i) => { r.status = reflow.receivers[i].status })

        // Divert non-mission-essential that couldn't get a tanker
        for (const rcv of receivers) {
          if (rcv.status === 'WAITING' && !rcv.missionEssential) {
            const hasAssignment = tankers.some(t =>
              t.queuedReceivers.includes(rcv.id) || t.currentReceiver === rcv.id
            )
            if (!hasAssignment) {
              rcv.status = 'DIVERTED'
              comms.push({
                simTime: state.currentTime, from: 'CONTROL', to: rcv.callsign,
                message: `${rcv.callsign}, not mission essential, RTB`,
                type: 'CONTINGENCY',
              })
            }
          }
        }

        // Check mission viability
        const viability = checkMissionViability({ ...state, tankers, receivers })
        if (!viability.viable) {
          missionAborted = true
          comms.push({
            simTime: state.currentTime, from: 'CONTROL', to: 'ALL',
            message: `MISSION ABORT — ${viability.reason}`,
            type: 'EMERGENCY',
          })
        } else if (viability.reason) {
          comms.push({
            simTime: state.currentTime, from: 'CONTROL', to: 'ALL',
            message: `WARNING — ${viability.reason}`,
            type: 'CONTINGENCY',
          })
        }
      }
      break
    }
    case 'FLIGHT_REDUCE': {
      const rcv = receivers.find(r => r.id === contingency.affectedId)
      if (rcv && rcv.flightSize > 1) {
        const reduceBy = contingency.reduceBy ?? 1
        const oldSize = rcv.flightSize
        rcv.flightSize = Math.max(1, rcv.flightSize - reduceBy)
        const ratio = rcv.flightSize / oldSize
        const freedOffload = rcv.requestedOffload - Math.round(rcv.requestedOffload * ratio)
        rcv.requestedOffload = Math.round(rcv.requestedOffload * ratio)
        rcv.burnRate = Math.round(rcv.burnRate * ratio)
        comms.push({
          simTime: state.currentTime, from: rcv.callsign, to: 'CONTROL',
          message: `${rcv.callsign}, flight reduced to ${rcv.flightSize} ship, ${Math.round(freedOffload / 1000)}K lbs offload freed`,
          type: 'CONTINGENCY',
        })
      }
      break
    }
  }

  let missionAbortedFlag = missionAborted

  // For TANKER_CANCEL: also check non-ME divert and mission viability
  if (contingency.type === 'TANKER_CANCEL') {
    // Divert non-mission-essential that have no tanker after reflow
    for (const rcv of receivers) {
      if (rcv.status === 'WAITING' && !rcv.missionEssential) {
        const hasAssignment = tankers.some(t =>
          t.queuedReceivers.includes(rcv.id) || t.currentReceiver === rcv.id
        )
        if (!hasAssignment) {
          rcv.status = 'DIVERTED'
          comms.push({
            simTime: state.currentTime, from: 'CONTROL', to: rcv.callsign,
            message: `${rcv.callsign}, not mission essential, RTB`,
            type: 'CONTINGENCY',
          })
        }
      }
    }
    const viability = checkMissionViability({ ...state, tankers, receivers })
    if (!viability.viable) {
      missionAbortedFlag = true
      comms.push({
        simTime: state.currentTime, from: 'CONTROL', to: 'ALL',
        message: `MISSION ABORT — ${viability.reason}`,
        type: 'EMERGENCY',
      })
    } else if (viability.reason) {
      comms.push({
        simTime: state.currentTime, from: 'CONTROL', to: 'ALL',
        message: `WARNING — ${viability.reason}`,
        type: 'CONTINGENCY',
      })
    }
  }

  return {
    ...state,
    tankers,
    receivers,
    missionAborted: state.missionAborted || missionAbortedFlag,
    contingencies: state.contingencies.map(c =>
      c.id === contingency.id ? { ...c, applied: true, triggerTime: state.currentTime } : c
    ),
    voiceLog: [...state.voiceLog, ...comms],
  }
}

// ===== Format sim time as T+MM:SS =====
export function formatSimTime(minutes: number): string {
  const m = Math.floor(minutes)
  const s = Math.round((minutes - m) * 60)
  return `T+${String(m).padStart(3, '0')}:${String(s).padStart(2, '0')}`
}

// ===== Fuel percentage helper =====
export function fuelPct(receiver: AARReceiver): number {
  return receiver.fuelCapacity > 0 ? (receiver.currentFuel / receiver.fuelCapacity) * 100 : 0
}

// ===== Fuel status color =====
export function fuelColor(pct: number): string {
  if (pct > 60) return '#22c55e'
  if (pct > 30) return '#f59e0b'
  return '#ef4444'
}

// ===== Offload percentage =====
export function offloadPct(tanker: AARTanker): number {
  return tanker.totalOffload > 0 ? (tanker.offloaded / tanker.totalOffload) * 100 : 0
}

// ===== Zulu time formatting =====
export function formatZuluTime(startZulu: string, simMinutes: number): string {
  const startH = parseInt(startZulu.slice(0, 2), 10) || 0
  const startM = parseInt(startZulu.slice(2, 4), 10) || 0
  const totalMin = startH * 60 + startM + Math.floor(simMinutes)
  const h = Math.floor(totalMin / 60) % 24
  const m = totalMin % 60
  return `${String(h).padStart(2, '0')}${String(m).padStart(2, '0')}z`
}

// ===== Mission viability check =====
export function checkMissionViability(state: AARSimState): { viable: boolean; reason?: string } {
  // Sum remaining tanker offload capacity for non-cancelled/RTB tankers
  let remainingCapacity = 0
  for (const tnk of state.tankers) {
    if (tnk.status === 'CANCELLED' || tnk.status === 'RTB') continue
    remainingCapacity += tnk.totalOffload - tnk.offloaded
  }

  // Sum mission-essential receiver fuel needs (not yet COMPLETE)
  let meFuelNeeded = 0
  for (const rcv of state.receivers) {
    if (!rcv.missionEssential) continue
    if (rcv.status === 'COMPLETE' || rcv.status === 'DIVERTED') continue
    meFuelNeeded += rcv.requestedOffload
  }

  if (meFuelNeeded === 0) return { viable: true }

  if (remainingCapacity < meFuelNeeded) {
    return {
      viable: false,
      reason: `insufficient tanker capacity (${Math.round(remainingCapacity / 1000)}K lbs remaining, ${Math.round(meFuelNeeded / 1000)}K lbs needed for mission-essential)`,
    }
  }

  // Marginal — less than 120% of needed
  if (remainingCapacity < meFuelNeeded * 1.2) {
    return {
      viable: true,
      reason: `tanker capacity marginal (${Math.round(remainingCapacity / 1000)}K lbs remaining for ${Math.round(meFuelNeeded / 1000)}K lbs needed)`,
    }
  }

  return { viable: true }
}
